import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { useSticky } from './sticky';

/**
 * 목록 머리글 정렬(2026-09-29 게시판 #37 1번) — 누르면 오름차순, 한 번 더 누르면 내림차순, 세 번째는 원래 순서.
 * 고른 정렬은 화면 메모리에 남는다(메뉴를 옮겼다 돌아와도 — `useSticky`).
 *
 * 🔴 **200줄씩 끊어 받는 목록은 서버가 정렬한다**(입금 내역·과태료 — `sort`/`dir` 을 넘긴다). 화면에서 정렬하면
 *    「전체를 금액순」이 아니라 「이 쪽만 금액순」이 되고 그 차이가 안 보인다(issues 107). 이 파일의 `sortRows` 는
 *    **전부 받아 둔 목록**(세금계산서·기사 오더·발행 요청)에만 쓴다.
 */
export type SortState = { key: string; dir: 'asc' | 'desc' } | null;

export function useSort(id: string): [SortState, (key: string) => void] {
  const [sort, setSort] = useSticky<SortState>(`sort.${id}`, null);
  const toggle = (key: string) =>
    setSort((cur) => (cur?.key !== key ? { key, dir: 'asc' } : cur.dir === 'asc' ? { key, dir: 'desc' } : null));
  return [sort, toggle];
}

/** 숫자처럼 생긴 값(금액 문자열 `12345.00` 포함)은 숫자로, 나머지는 한국어 순으로. 빈 값은 늘 맨 뒤. */
function cmp(a: unknown, b: unknown): number {
  const empty = (v: unknown) => v === null || v === undefined || v === '';
  if (empty(a) || empty(b)) return empty(a) && empty(b) ? 0 : empty(a) ? 1 : -1;
  const na = typeof a === 'number' ? a : typeof a === 'string' && /^-?[0-9]+(\.[0-9]+)?$/.test(a) ? Number(a) : NaN;
  const nb = typeof b === 'number' ? b : typeof b === 'string' && /^-?[0-9]+(\.[0-9]+)?$/.test(b) ? Number(b) : NaN;
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  // numeric — 발행번호 `2026-1-9` 가 `2026-1-10` 앞에 오게.
  return String(a).localeCompare(String(b), 'ko', { numeric: true });
}

export function sortRows<T>(rows: readonly T[], sort: SortState, get: (row: T, key: string) => unknown = (r, k) => (r as Record<string, unknown>)[k]): T[] {
  if (!sort) return rows as T[];
  const sign = sort.dir === 'asc' ? 1 : -1;
  // 빈 값은 방향과 상관없이 맨 뒤 — 내림차순에서 빈 칸이 맨 위로 올라오면 목록이 빈 것처럼 보인다.
  return rows.map((r, i) => ({ r, i })).sort((x, y) => {
    const a = get(x.r, sort.key), b = get(y.r, sort.key);
    const ea = a === null || a === undefined || a === '', eb = b === null || b === undefined || b === '';
    if (ea || eb) return ea === eb ? x.i - y.i : ea ? 1 : -1;
    return cmp(a, b) * sign || x.i - y.i;
  }).map((x) => x.r);
}

export function useSortedRows<T>(rows: readonly T[] | undefined, sort: SortState, get?: (row: T, key: string) => unknown): T[] {
  return useMemo(() => sortRows(rows ?? [], sort, get), [rows, sort, get]);
}

/** 손으로 짠 표의 머리글 칸. `sortKey` 가 없으면 보통 칸. */
export function SortTh({ sortKey, sort, onSort, className = '', children, fixed = false, after }: {
  sortKey?: string; sort: SortState; onSort: (key: string) => void; className?: string; children: ReactNode;
  /** 칸 폭을 사용자가 정해 둔 표(`table-fixed`) — 아래 「줄지 않게」 잠금을 끈다(잠그면 좁히기가 안 된다 · #208). */
  fixed?: boolean;
  /** 버튼 **밖**에 붙일 것(폭 끄는 손잡이 — 버튼 안에 두면 끈 뒤 클릭이 정렬로 샌다). */
  after?: ReactNode;
}) {
  // 🔴 칸 너비는 **줄지 않게** 붙잡는다(2026-09-30 게시판 #75 — 「정렬을 누르면 머리글 글자가 움직인다」).
  //    200줄씩 받는 목록은 정렬마다 다른 줄이 와서 자동 너비가 바뀌고, 그때마다 머리글이 좌우로 밀렸다.
  //    처음 잰 너비를 min-width 로 두고 더 넓어질 때만 늘린다(DOM 직접 — 다시 그리기 없음).
  const ref = useRef<HTMLTableCellElement>(null);
  const widest = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (fixed) { widest.current = 0; el.style.minWidth = ''; return; }
    const w = Math.ceil(el.getBoundingClientRect().width);
    if (w > widest.current) { widest.current = w; el.style.minWidth = `${w}px`; }
  });
  const cls = `${className} ${after ? 'relative' : ''}`;
  if (!sortKey) return <th ref={ref} className={cls}>{fixed ? <span className="block truncate">{children}</span> : children}{after}</th>;
  const on = sort?.key === sortKey;
  return (
    <th ref={ref} className={cls} aria-sort={on ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" className={`inline-flex max-w-full items-center gap-1 hover:text-brand-700 ${className.includes('text-right') ? 'flex-row-reverse' : ''}`}
              onClick={() => onSort(sortKey)} title="눌러서 오름차순 → 내림차순 → 원래 순서">
        <span className={fixed ? 'truncate' : undefined}>{children}</span>
        <span className={`inline-block w-3 shrink-0 text-center text-[10px] ${on ? 'text-brand-700' : 'text-ink-faint/40'}`}>{on ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
      {after}
    </th>
  );
}
